import { useEffect, useState } from "react";

type UseDynamicScriptOptions = {
  removeOnUnmount?: boolean;
};

type UseDynamicScriptStatuses = "idle" | "loading" | "success" | "error";

const cachedScriptStatuses = new Map<string, UseDynamicScriptStatuses>();

const getScriptElement = (scriptUrl: string) =>
  document.querySelector(`script[src="${scriptUrl}"]`) as HTMLScriptElement;

export const addScriptToPage = (scriptUrl: string): HTMLScriptElement => {
  const script = document.createElement("script");

  script.src = scriptUrl;
  script.async = true;

  document.body.appendChild(script);

  const setAttributeFromEvent = (event: Event) => {
    const scriptStatus: UseDynamicScriptStatuses = event.type === "load" ? "success" : "error";

    script?.setAttribute("data-status", scriptStatus);
  };

  script.addEventListener("load", setAttributeFromEvent);
  script.addEventListener("error", setAttributeFromEvent);

  return script;
};

export const useDynamicScript = (scriptUrl: string, { removeOnUnmount }: UseDynamicScriptOptions | undefined = {}) => {
  const [status, setStatus] = useState<UseDynamicScriptStatuses>(cachedScriptStatuses.get(scriptUrl) ?? "idle");
  const [isScriptLoading, setScriptLoading] = useState(cachedScriptStatuses.get(scriptUrl) === "loading");
  const [isScriptLoaded, setScriptLoaded] = useState(cachedScriptStatuses.get(scriptUrl) === "success");
  const [isError, setError] = useState(cachedScriptStatuses.get(scriptUrl) === "error");

  useEffect(() => {
    if (!scriptUrl) {
      return;
    }

    const cachedScriptStatus = cachedScriptStatuses.get(scriptUrl);

    if (cachedScriptStatus === "success" || cachedScriptStatus === "error") {
      setStatus(cachedScriptStatus);

      return;
    }

    const existingScriptElement = getScriptElement(scriptUrl);
    const script = existingScriptElement ?? addScriptToPage(scriptUrl);

    if (existingScriptElement) {
      setStatus(cachedScriptStatus ?? "loading");
    } else {
      setStatus("loading");
    }

    const setStateFromEvent = (event: Event) => {
      const newStatus = event.type === "load" ? "success" : "error";
      setStatus(newStatus);
      cachedScriptStatuses.set(scriptUrl, newStatus);
    };

    script.addEventListener("load", setStateFromEvent);
    script.addEventListener("error", setStateFromEvent);

    return () => {
      const script = getScriptElement(scriptUrl);

      if (script && removeOnUnmount) {
        document.body.removeChild(script);
        cachedScriptStatuses.delete(scriptUrl);
      }
    };
  }, [scriptUrl, removeOnUnmount]);

  useEffect(() => {
    if (status === "loading") {
      setScriptLoading(true);
      setScriptLoaded(false);
      setError(false);
      cachedScriptStatuses.set(scriptUrl, "loading");
    }

    if (status === "success") {
      setScriptLoaded(true);
      setScriptLoading(false);
      setError(false);
      cachedScriptStatuses.set(scriptUrl, "success");
    }

    if (status === "error") {
      setScriptLoaded(false);
      setScriptLoading(false);
      setError(true);
      cachedScriptStatuses.set(scriptUrl, "error");
    }
  }, [status]);

  return {
    isScriptLoading,
    isScriptLoaded,
    isError,
    status,
  };
};
